import { $Rate } from './links';

/** Parse chip values like "12", "25+" or "10+" */
export const parseContentCount = (value) => {
    if (!value) return 0;
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
};

const isNone = (value) => !value || value === 'none';

/** Per photoshoot session (ILS), spread across months by frequency */
export const PHOTOSHOOT_SESSION_ILS = 2500;

/**
 * Paid ads management fee (ILS / month) — Meta ad spend is separate.
 * Scales with daily media budget (higher spend → more management).
 */
export const ADS_MGMT_BY_DAILY_BUDGET = {
    '50': 350,
    '100': 450,
    '200': 700,
    '300': 850,
    '400+': 1000,
};

export const parseAdsDailyBudget = (value) => {
    if (!value) return 0;
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
};

export const getAdsMonthlyIls = (ads, adsDailyBudget) => {
    if (isNone(ads) || ads !== 'yes') return 0;
    if (!adsDailyBudget) return 0;
    return ADS_MGMT_BY_DAILY_BUDGET[adsDailyBudget] || 0;
};

export const getPhotoshootMonthlyIls = (photoshoot) => {
    switch (photoshoot) {
        case '1month':
            return PHOTOSHOOT_SESSION_ILS;
        case '2month':
            return PHOTOSHOOT_SESSION_ILS * 2;
        case 'every2':
            return PHOTOSHOOT_SESSION_ILS / 2;
        case 'every3':
            return PHOTOSHOOT_SESSION_ILS / 3;
        default:
            return 0;
    }
};

/** User picked posts, reels & stories (including explicit "0") */
export const hasAllCountSelections = ({ posts, reels, stories }) =>
    posts !== '' && reels !== '' && stories !== '';

/** At least one paid line item */
export const hasBillableSelection = ({
    posts,
    reels,
    stories,
    photoshoot,
    ads,
    website,
}) => {
    const content =
        parseContentCount(posts) +
        parseContentCount(reels) +
        parseContentCount(stories);

    if (content > 0) return true;
    if (!isNone(photoshoot)) return true;
    if (!isNone(ads)) return true;
    if (!isNone(website)) return true;
    return false;
};

export const hasMinimumForEstimate = (selection) => {
    if (!hasAllCountSelections(selection) || !hasBillableSelection(selection)) {
        return false;
    }
    if (selection.ads === 'yes' && !selection.adsDailyBudget) return false;
    return true;
};

/**
 * Monthly estimate (ILS) calibrated from published plan tiers.
 * Returns null when nothing billable is selected.
 */
export const estimateCustomPlanMonthly = ({
    posts,
    reels,
    stories,
    photoshoot,
    ads,
    adsDailyBudget,
    website,
}) => {
    if (!hasBillableSelection({ posts, reels, stories, photoshoot, ads, website })) {
        return null;
    }

    let total = 0;

    const postCount = parseContentCount(posts);
    const reelCount = parseContentCount(reels);
    const storyCount = parseContentCount(stories);
    const contentCount = postCount + reelCount + storyCount;

    if (contentCount > 0) {
        total += 350;
        total += postCount * 46;
        total += storyCount * 26;
        total += reelCount * 125;
    }

    total += getPhotoshootMonthlyIls(photoshoot);

    total += getAdsMonthlyIls(ads, adsDailyBudget);

    const websiteMonthly = {
        none: 0,
        included: 260,
        existing: 170,
    };
    total += websiteMonthly[website] || 0;

    if (total <= 0) return null;

    const estimate = Math.ceil(total / 50) * 50;
    const min = Math.max(estimate, Math.ceil((estimate * 0.92) / 50) * 50);
    const max = Math.ceil((estimate * 1.1) / 50) * 50;

    return { estimate, min, max: Math.max(min, max) };
};

export const formatPlanMoney = (amountIls, cur, isYearly) => {
    let amount = amountIls;
    if (isYearly) {
        amount = Math.round(amount * 11.5);
    }

    if (cur === '$') {
        const aed = Math.ceil(amount / $Rate / 50) * 50;
        return `$${aed.toLocaleString()}`;
    }

    const ils = Math.ceil(amount / 50) * 50;
    return `₪${ils.toLocaleString()}`;
};

export const isReadyToSubmit = ({
    posts,
    reels,
    stories,
    photoshoot,
    ads,
    adsDailyBudget,
    website,
}) => {
    if (!hasAllCountSelections({ posts, reels, stories })) return false;
    if (!photoshoot || !ads || !website) return false;
    if (!hasBillableSelection({ posts, reels, stories, photoshoot, ads, website })) {
        return false;
    }
    if (ads === 'yes' && !adsDailyBudget) return false;
    return true;
};
